import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
// MUI
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ConstructionIcon from '@mui/icons-material/Construction';
import ErrorIcon from '@mui/icons-material/Error';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';

// self-made
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';
import CloseIcon from '@mui/icons-material/Close';
import { formatBytes, calculateRemainingTime } from 'utils/misc';
import { getBadge, getMissionTypeDisplayString } from './util';
import { MISSION, MISSION_STATUS } from 'shared/constants/uploadAttachments';
import logger from 'utils/logger';

const log = logger.createLogger('UploadMissionCard');

function UploadMissionCard({
  mission,
  closeMenu,
  setAbortUploadWarningModalIsOpen,
  setMissionIDToBeAborted,
  setMissionToBeRemoved,
}) {
  const progress = mission.uploadingProgress;
  const remainingTime = calculateRemainingTime(
    mission.batchTotalBytes,
    mission.uploadedBytes,
    mission.uploadingSpeed
  );
  const missionStatus = mission.status;

  const handleAbortButtonClick = (e) => {
    e.stopPropagation();
    const id =
      mission.type === MISSION.ISSUE
        ? mission.issueIdentifierId
        : mission.missionId;
    setMissionIDToBeAborted(id);
    setAbortUploadWarningModalIsOpen(true);
  };

  const handleDeleteButtonClick = (e) => {
    e.stopPropagation();
    const id =
      mission.type === MISSION.ISSUE
        ? mission.issueIdentifierId
        : mission.missionId;
    setMissionToBeRemoved(id);
    closeMenu();
  };

  const handleMinimizeButtonClick = (e) => {
    e.stopPropagation();
    closeMenu();
  };

  return (
    <Card variant="outlined" sx={{ width: '100%', p: 2 }}>
      <Grid container justify="flex-end">
        <Grid size={6}>{getBadge(missionStatus, 'sm')}</Grid>
        {missionStatus === MISSION_STATUS.UPLOADING && (
          <Grid size={5} display="flex" justifyContent="flex-end">
            <MDButton
              size="small"
              width="50px"
              variant="contained"
              color="error"
              sx={{ height: 8 }}
              onClick={handleAbortButtonClick}>
              {intl.get('upload_mission_menu_item_abort')}
            </MDButton>
          </Grid>
        )}

        {[
          MISSION_STATUS.UPLOAD_ABORTED,
          MISSION_STATUS.UPLOAD_FAILED,
          MISSION_STATUS.PROCESS_FAILED,
          MISSION_STATUS.PROCESS_COMPLETE,
        ].includes(missionStatus) ? (
          <Grid size={6} display="flex" justifyContent="flex-end">
            <IconButton
              aria-label="delete"
              size="small"
              variant="empty"
              onClick={handleDeleteButtonClick}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Grid>
        ) : (
          <Grid
            size={missionStatus === MISSION_STATUS.UPLOADING ? 1 : 6}
            display="flex"
            justifyContent="flex-end">
            <IconButton
              aria-label="minimize"
              size="small"
              variant="empty"
              onClick={handleMinimizeButtonClick}>
              <HorizontalRuleIcon fontSize="small" />
            </IconButton>
          </Grid>
        )}
      </Grid>

      <MDBox mt={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <MDTypography variant="body2" color="text" fontWeight="bold">
            {`${intl.get('upload_mission_menu_item_type')}: `}
          </MDTypography>
          <MDTypography
            variant="body2"
            color="text"
            style={{ whiteSpace: 'normal' }}
            sx={{ width: '70%' }}>
            {getMissionTypeDisplayString(mission.type)}
          </MDTypography>
        </Stack>
        {mission.type === MISSION.ISSUE && (
          <Stack direction="row" spacing={1} alignItems="center">
            <MDTypography variant="body2" color="text" fontWeight="bold">
              {`${intl.get('upload_mission_menu_item_sent_to')}:`}
            </MDTypography>
            <MDTypography
              variant="body2"
              color="text"
              style={{ whiteSpace: 'normal' }}
              sx={{ width: '70%' }}>
              {`${mission.formState.dstOrgName}`}
            </MDTypography>
          </Stack>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <MDTypography variant="body2" color="text" fontWeight="bold">
            {`${intl.get('upload_mission_menu_item_issue_title')}:`}
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {`${mission.issueIdentifierId}`}
          </MDTypography>
        </Stack>
      </MDBox>

      {missionStatus === MISSION_STATUS.UPLOADING ? (
        <MDBox mb={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <MDTypography variant="body2" color="text" fontWeight="bold">
              {`${intl.get('upload_mission_menu_item_uploading_file')}:`}
            </MDTypography>
            <MDTypography
              variant="body2"
              color="text"
              style={{ whiteSpace: 'normal' }}
              sx={{ width: '70%' }}>
              {`${mission.uploadingFileName}`}
            </MDTypography>
          </Stack>

          {/* Progress bar */}
          <MDBox display="flex" alignItems="center">
            <MDBox mt={0.5} width="100%">
              <MDProgress variant="gradient" value={progress} color="info" />
            </MDBox>
            <MDTypography variant="body3" ml={1}>
              {`${Math.round(progress)}%`}
            </MDTypography>
          </MDBox>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <MDTypography variant="body3">
              {`${intl.get(
                'upload_mission_menu_item_upload_speed'
              )}: ${formatBytes(mission.uploadingSpeed)}/Sec`}
            </MDTypography>
            <MDTypography variant="body3" mr={1}>
              {`${intl.get(
                'upload_mission_menu_item_upload_time_remaining'
              )}: ${remainingTime}`}
            </MDTypography>
          </MDBox>
        </MDBox>
      ) : (
        <></>
      )}

      {missionStatus === MISSION_STATUS.PROCESSING ? (
        <MDBox my={1} display="flex" alignItems="center">
          <HourglassTopIcon color="info" fontSize="small" sx={{ mx: 1 }} />
          <MDTypography variant="body2" fontWeight="bold" color="info">
            {intl.get('upload_mission_menu_item_message_processing')}
          </MDTypography>
        </MDBox>
      ) : (
        <></>
      )}

      {missionStatus === MISSION_STATUS.UPLOAD_FAILED ? (
        <MDBox mb={1} display="flex" alignItems="center">
          <ErrorIcon color="error" fontSize="small" sx={{ mx: 1 }} />
          <MDTypography variant="body2" fontWeight="bold" color="error">
            {intl.get('upload_mission_menu_item_message_upload_failed')}
          </MDTypography>
        </MDBox>
      ) : (
        <></>
      )}

      {missionStatus === MISSION_STATUS.PROCESS_FAILED ? (
        <MDBox mb={1} display="flex" alignItems="center">
          <ConstructionIcon color="error" fontSize="small" sx={{ mx: 1 }} />
          <MDTypography variant="body2" fontWeight="bold" color="error">
            {intl.get('upload_mission_menu_item_message_process_failed')}
          </MDTypography>
        </MDBox>
      ) : (
        <></>
      )}
    </Card>
  );
}

// Typechecking props for the UploadMissionCard
UploadMissionCard.propTypes = {
  closeMenu: PropTypes.func.isRequired,
  mission: PropTypes.shape({
    type: PropTypes.string,
    batchTotalBytes: PropTypes.number,
    selectedTagIds: PropTypes.array,
    tagList: PropTypes.array,
    formState: PropTypes.object,
    issueCreatedAt: PropTypes.string,
    issueId: PropTypes.number,
    issueIdentifierId: PropTypes.string,
    missionId: PropTypes.string,
    status: PropTypes.oneOf([
      MISSION_STATUS.INIT,
      MISSION_STATUS.PROCESS_COMPLETE,
      MISSION_STATUS.UPLOADING,
      MISSION_STATUS.PROCESSING,
      MISSION_STATUS.UPLOAD_FAILED,
      MISSION_STATUS.PROCESS_FAILED,
      MISSION_STATUS.UPLOAD_ABORTED,
    ]),
    structuredAttachments: PropTypes.array,
    timestamp: PropTypes.number,
    uploadedBytes: PropTypes.number,
    uploadingFileName: PropTypes.string,
    uploadingProgress: PropTypes.number,
    uploadingSpeed: PropTypes.number,
  }),
  setAbortUploadWarningModalIsOpen: PropTypes.func,
  setMissionIDToBeAborted: PropTypes.func,
  setMissionToBeRemoved: PropTypes.func,
};

export default UploadMissionCard;
